import React, { useState } from 'react';
import { putDataAPI, deleteDataAPI } from '../../../utils/fetchData';
import { useModal } from '../../../hooks/useModal';
import Modal from '../../../components/Modal';

const HotelDetailModal = ({ hotel, show, onClose, onRefresh }) => {
  const { modalConfig, showModal, closeModal } = useModal();
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState(hotel.status || 'option');

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const formatPrice = (amount) => {
    if (!amount || amount === 0) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'option': { label: 'OPTION', color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.1)' },
      'selected': { label: 'SELECTED', color: '#FF9500', bg: 'rgba(255,149,0,0.2)' },
      'booked': { label: 'BOOKED', color: '#34C759', bg: 'rgba(52,199,89,0.2)' },
      'cancelled': { label: 'CANCELLED', color: '#FF3B30', bg: 'rgba(255,59,48,0.2)' }
    };
    return badges[status] || badges['option'];
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      await putDataAPI(`user/tm/hotels/${hotel._id}`, { status: newStatus }, token);
      
      setStatus(newStatus);
      showModal('Status Updated', `Hotel status changed to ${newStatus}`, 'success');
      
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showModal('Error', 'Failed to update hotel status', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      await deleteDataAPI(`user/tm/hotels/${hotel._id}`, token);
      
      showModal('Hotel Removed', 'Hotel has been removed from your trip', 'success');
      
      setTimeout(() => {
        if (onRefresh) onRefresh();
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error deleting hotel:', error);
      showModal('Error', 'Failed to remove hotel', 'error');
    }
  };

  const confirmDelete = () => {
    showModal(
      'Remove Hotel?',
      'Are you sure you want to remove this hotel from your trip?',
      'warning',
      handleDelete,
      true
    );
  };

  const statusBadge = getStatusBadge(status);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
        <div 
          className="w-full max-w-[400px] h-[600px] mx-4 transform overflow-hidden rounded-lg bg-black border border-[rgba(255,255,255,0.1)] text-left align-middle shadow-xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-3 flex-1">
                {/* Hotel Image Thumbnail */}
                <div className="w-12 h-12 bg-[rgba(255,255,255,0.1)] rounded-[8px] overflow-hidden flex-shrink-0">
                  {hotel.logo ? (
                    <img
                      src={hotel.logo}
                      alt={hotel.hotelName || hotel.hotel_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.style.backgroundColor = 'rgba(255,255,255,0.1)';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-[rgba(255,255,255,0.1)]"></div>
                  )}
                </div>
                
                {/* Hotel Info */}
                <div className="flex-1">
                  <h2 className="text-white text-[14px] font-medium mb-1">
                    {hotel.hotelName || hotel.hotel_name}
                  </h2>
                  {hotel.chain && (
                    <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                      {hotel.chain}
                    </p>
                  )}
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="text-white text-[12px] font-normal hover:opacity-70"
              >
                Close
              </button>
            </div>
            
            {/* Status Badge */}
            <div className="mb-3">
              <span 
                className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-wider"
                style={{
                  color: statusBadge.color,
                  backgroundColor: statusBadge.bg
                }}
              >
                {statusBadge.label}
              </span>
            </div>

            {/* Address */}
            {(hotel.address || hotel.city) && (
              <div className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.4] mb-2">
                {hotel.address?.street && <span>{hotel.address.street}, </span>}
                <span>
                  {hotel.address?.city || hotel.city}
                  {(hotel.address?.state || hotel.state) && `, ${hotel.address.state || hotel.state}`}
                  {(hotel.address?.country || hotel.country) && ` • ${hotel.address.country || hotel.country}`}
                </span>
              </div>
            )}

            {/* Contact Info */}
            {hotel.contactInfo?.phone && (
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                Phone: {hotel.contactInfo.phone}
              </p>
            )}
            {hotel.contactInfo?.email && (
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                Email: {hotel.contactInfo.email}
              </p>
            )}
          </div>

          {/* Hotel Details - Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-4">
            {/* Dates */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-4">
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">Check-in</p>
                  <p className="text-white font-semibold">{formatDate(hotel.checkIn)}</p>
                  <p className="text-[rgba(255,255,255,0.6)] text-[10px] mt-1">
                    {formatTime(hotel.checkIn)}
                  </p>
                </div>
                <div>
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">Check-out</p>
                  <p className="text-white font-semibold">{formatDate(hotel.checkOut)}</p>
                  <p className="text-[rgba(255,255,255,0.6)] text-[10px] mt-1">
                    {formatTime(hotel.checkOut)}
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing */}
            {hotel.pricing && (
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-4">
                <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">Pricing</p>
                <div className="flex items-center justify-between">
                  <div>
                    {hotel.pricing.perNight && (
                      <p className="text-white text-[16px] font-bold">
                        {formatPrice(hotel.pricing.perNight)}
                        <span className="text-[11px] text-[rgba(255,255,255,0.5)] font-normal ml-1">
                          /night
                        </span>
                      </p>
                    )}
                  </div>
                  {hotel.pricing.total && (
                    <div className="text-right">
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px]">Total</p>
                      <p className="text-[#34C759] text-[14px] font-bold">
                        {formatPrice(hotel.pricing.total)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Room Details */}
            {(hotel.roomType || hotel.numberOfRooms > 0 || hotel.roomNumber) && (
              <div className="space-y-2 text-[12px]">
                {hotel.roomType && (
                  <div className="flex justify-between">
                    <span className="text-[rgba(255,255,255,0.6)]">Room Type</span>
                    <span className="text-white">{hotel.roomType}</span>
                  </div>
                )}
                {hotel.numberOfRooms && (
                  <div className="flex justify-between">
                    <span className="text-[rgba(255,255,255,0.6)]">Number of Rooms</span>
                    <span className="text-white">{hotel.numberOfRooms}</span>
                  </div>
                )}
                {hotel.roomNumber && (
                  <div className="flex justify-between">
                    <span className="text-[rgba(255,255,255,0.6)]">Room Number</span>
                    <span className="text-white">{hotel.roomNumber}</span>
                  </div>
                )}
                {hotel.confirmationNumber && (
                  <div className="flex justify-between">
                    <span className="text-[rgba(255,255,255,0.6)]">Confirmation #</span>
                    <span className="text-white font-mono">{hotel.confirmationNumber}</span>
                  </div>
                )}
              </div>
            )}

            {/* Amenities */}
            {hotel.amenities && hotel.amenities.length > 0 && (
              <div>
                <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">Amenities</p>
                <div className="flex flex-wrap gap-2">
                  {hotel.amenities.map((amenity, index) => (
                    <span
                      key={index}
                      className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] px-2 py-1 rounded text-[10px]"
                    >
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {hotel.notes && (
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-3">
                <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">Notes</p>
                <p className="text-white text-[11px] leading-relaxed">{hotel.notes}</p>
              </div>
            )}

            {/* Status Selector */}
            <div>
              <label className="block text-white text-[12px] font-medium mb-2">Booking Status</label>
              <div className="grid grid-cols-2 gap-2">
                {['option', 'selected', 'booked', 'cancelled'].map((statusOption) => (
                  <button
                    key={statusOption}
                    onClick={() => !updating && handleStatusChange(statusOption)}
                    disabled={updating}
                    className={`px-3 py-2 rounded-[8px] text-[12px] font-medium transition-all border ${
                      status === statusOption
                        ? 'bg-[#FF9500] text-black border-[#FF9500]'
                        : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,149,0,0.1)] hover:border-[rgba(255,149,0,0.3)]'
                    } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {updating && status === statusOption ? 'Updating...' : statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {hotel.notes && hotel.notes.includes('http') && (
                <button
                  onClick={() => {
                    const urlMatch = hotel.notes.match(/(https?:\/\/[^\s]+)/);
                    if (urlMatch) window.open(urlMatch[0], '_blank');
                  }}
                  className="w-full bg-[#FF9500] hover:bg-[#FF8800] text-black text-[12px] font-medium py-2 px-4 rounded-[8px] transition-all focus:outline-none"
                >
                  View Booking
                </button>
              )}
              <button
                onClick={confirmDelete}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white rounded-[8px] px-3 py-2 text-[12px] font-medium transition-colors hover:bg-[rgba(255,255,255,0.1)]"
              >
                Remove Hotel
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </>
  );
};

export default HotelDetailModal;

